import React from 'react';
import { useTranslation } from "react-i18next";
import { format, parseISO } from 'date-fns';
import { TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { ChevronRight, RefreshCw } from 'lucide-react';
import { fmtHours, fmtMinutes, getDateFnsLocale } from '../../utils/attendanceUtils';
import StatusBadge from './StatusBadge';

const AttendanceSummaryTable = ({
  summaryRows,
  absenceReasons,
  expandedEmployees,
  toggleEmployeeExpand,
  runCalculation,
  calculating,
  dateFrom,
  dateTo,
}) => {
  const { t, i18n } = useTranslation();
  const dateLocale = getDateFnsLocale(i18n.language);
  return (
    <table className="w-full caption-bottom text-sm">
      <TableHeader className="sticky top-0 z-10 shadow-sm">
        <TableRow className="bg-muted/50 shadow-sm">
          <TableHead className="w-8"></TableHead>
          <TableHead className="w-28">{t("attComp.matricule")}</TableHead>
          <TableHead>{t("attComp.summary.colName")}</TableHead>
          <TableHead className="text-center">{t("attComp.summary.days")}</TableHead>
          <TableHead className="text-center text-green-500">{t("attComp.summary.present")}</TableHead>
          <TableHead className="text-center text-red-500">{t("attComp.summary.absent")}</TableHead>
          <TableHead className="text-center text-orange-500">{t("attComp.summary.late")}</TableHead>
          <TableHead className="text-center text-yellow-500">{t("attComp.summary.earlyDep")}</TableHead>
          <TableHead className="text-center text-muted-foreground">{t("attComp.summary.noSchedule")}</TableHead>
          <TableHead className="text-center text-purple-500">{t("attComp.summary.holidays")}</TableHead>
          <TableHead className="text-center">{t("attComp.summary.worked")}</TableHead>
          <TableHead className="text-center">{t("attComp.summary.totalLate")}</TableHead>
          <TableHead className="text-center">{t("attComp.summary.rate")}</TableHead>
          <TableHead className="w-10"></TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {summaryRows.length === 0 ? (
          <TableRow>
            <TableCell colSpan={14} className="text-center py-12 text-muted-foreground">{t("attComp.noResult")}</TableCell>
          </TableRow>
        ) : (
          summaryRows.map(s => {
            const isExpanded = expandedEmployees.has(s.employee_id);
            const workingDays = s.days - s.no_schedule - s.public_holiday - s.day_off;
            const presenceRate = workingDays > 0 ? Math.round((s.present / workingDays) * 100) : null;
            const rateColor = presenceRate == null ? 'text-gray-400'
              : presenceRate >= 90 ? 'text-green-600'
              : presenceRate >= 70 ? 'text-yellow-600' : 'text-red-600';

            return (
              <React.Fragment key={s.employee_id}>
                <TableRow
                  className="cursor-pointer hover:bg-accent transition-colors font-medium"
                  onClick={() => toggleEmployeeExpand(s.employee_id)}>
                  <TableCell className="w-8 px-2">
                    <ChevronRight className={`h-4 w-4 text-muted-foreground transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''}`} />
                  </TableCell>
                  <TableCell className="font-mono text-xs">{s.registration_number}</TableCell>
                  <TableCell>
                    <span className="font-semibold">{s.last_name}</span>{' '}
                    <span className="text-muted-foreground">{s.first_name}</span>
                  </TableCell>
                  <TableCell className="text-center font-semibold">{s.days}</TableCell>
                  <TableCell className="text-center">
                    <span className="text-green-500 font-semibold">{s.present}</span>
                  </TableCell>
                  <TableCell className="text-center">
                    {s.absent > 0 ? <span className="text-red-600 font-semibold">{s.absent}</span> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {s.late > 0 ? <span className="text-orange-600 font-semibold">{s.late}</span> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {s.early > 0 ? <span className="text-yellow-600 font-semibold">{s.early}</span> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {s.no_schedule > 0 ? <span className="text-muted-foreground font-semibold">{s.no_schedule}</span> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {s.public_holiday > 0 ? <span className="text-purple-600 font-semibold">{s.public_holiday}</span> : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex flex-col items-center gap-0.5">
                      <span className="font-mono font-semibold text-sm">{fmtHours(Math.round(s.worked * 100) / 100)}</span>
                      {s.expected > 0 && (
                        <div className="w-16 h-1.5 bg-muted rounded-full overflow-hidden">
                          <div
                            className={`h-full rounded-full ${(s.worked / s.expected) >= 0.9 ? 'bg-green-500' : (s.worked / s.expected) >= 0.7 ? 'bg-yellow-400' : 'bg-red-400'}`}
                            style={{ width: `${Math.min(100, (s.worked / s.expected) * 100)}%` }}
                          />
                        </div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-center">
                    {s.lateness > 0
                      ? <span className="text-red-600 font-semibold text-sm">+{fmtMinutes(s.lateness)}</span>
                      : <span className="text-green-600 text-sm">✓</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    {presenceRate != null
                      ? <span className={`font-bold text-sm ${rateColor}`}>{presenceRate}%</span>
                      : <span className="text-muted-foreground">—</span>}
                  </TableCell>
                  <TableCell className="px-1" onClick={e => e.stopPropagation()}>
                    <button
                      title={t("attComp.summary.recalcTitle")}
                      onClick={() => runCalculation({ date_from: dateFrom, date_to: dateTo, employee_ids: [s.employee_id], recalculate: true })}
                      disabled={calculating}
                      className="p-1.5 rounded hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                      <RefreshCw className={`h-3.5 w-3.5 ${calculating ? 'animate-spin' : ''}`} />
                    </button>
                  </TableCell>
                </TableRow>

                {isExpanded && s.day_records
                  .slice()
                  .sort((a, b) => a.date.localeCompare(b.date))
                  .map((r) => {
                    const ar = r.absence_reason_id ? absenceReasons.find(x => x.id === r.absence_reason_id) : null;
                    const isAbs = r.status === 'absent' || r.status === 'absence_justifiee';
                    const wh = r.status === 'public_holiday'
                      ? (r.expected_hours != null ? fmtHours(r.expected_hours) : '—')
                      : (ar && isAbs) ? (ar.is_paid ? fmtHours(r.expected_hours) : '0h')
                      : (r.worked_hours != null ? fmtHours(r.worked_hours) : '—');
                    return (
                      <TableRow key={`${s.employee_id}-${r.date}`}
                        className={`border-l-2 border-border bg-muted/20 text-sm
                          ${r.status === 'absent' || r.status === 'absence_justifiee' ? 'bg-red-950/20' : ''}
                          ${r.status === 'public_holiday' ? 'bg-purple-950/20' : ''}`}>
                        <TableCell></TableCell>
                        <TableCell className="text-xs text-muted-foreground pl-4 py-2">
                          {format(parseISO(r.date + 'T00:00:00'), 'EEE dd/MM', { locale: dateLocale })}
                        </TableCell>
                        <TableCell colSpan={2} className="py-2">
                          <div className="flex items-center gap-2">
                            <StatusBadge status={r.status} />
                            {r.schedule_name && <span className="text-xs text-muted-foreground">{r.schedule_name}</span>}
                            {r.absence_reason_name && (
                              <span className="text-xs font-medium" style={{ color: r.absence_reason_color }}>· {r.absence_reason_name}</span>
                            )}
                            {r.is_holiday && r.holiday_name && (
                              <span className="text-xs text-purple-600">· {r.holiday_name}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-center py-2 font-mono text-xs text-foreground">{r.actual_entry || '—'}</TableCell>
                        <TableCell className="text-center py-2 font-mono text-xs text-foreground">{r.actual_exit || '—'}</TableCell>
                        <TableCell className="text-center py-2">
                          {r.lateness_minutes > 0 ? <span className="text-red-600 font-semibold text-xs">+{fmtMinutes(r.lateness_minutes)}</span> : null}
                        </TableCell>
                        <TableCell className="text-center py-2">
                          {r.early_departure_minutes > 0 ? <span className="text-orange-600 font-semibold text-xs">-{fmtMinutes(r.early_departure_minutes)}</span> : null}
                        </TableCell>
                        <TableCell></TableCell>
                        <TableCell></TableCell>
                        <TableCell className="text-center py-2 font-mono font-semibold text-xs">{wh}</TableCell>
                        <TableCell className="text-center py-2">
                          {r.overtime_hours > 0 ? <span className="text-purple-600 font-semibold text-xs">+{fmtHours(r.overtime_hours)}</span> : null}
                        </TableCell>
                        <TableCell colSpan={2}></TableCell>
                      </TableRow>
                    );
                  })}
              </React.Fragment>
            );
          })
        )}
      </TableBody>
    </table>
  );
};

export default AttendanceSummaryTable;
